import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, Building2, Package, CalendarCheck, CreditCard, LineChart,
  Activity, ArrowRight
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import StatCard from '../../components/StatCard';
import StatusBadge from '../../components/StatusBadge';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorState from '../../components/ErrorState';
import * as analytics from '../../api/analytics.api';
import { errorMessage } from '../../api/client';
import { formatMoney, formatNumber } from '../../utils/format';
import { useAuth } from '../../hooks/useAuth';

function SuperAdminDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [revenueSeries, setRevenueSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [s, r] = await Promise.all([
          analytics.summary(),
          analytics.revenue({ groupBy: 'day' })
        ]);
        if (!cancelled) {
          setData(s);
          setRevenueSeries(r?.series || []);
        }
      } catch (err) {
        if (!cancelled) setError(errorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <LoadingSpinner size="lg" label="Loading control center…" />;
  if (error)   return <ErrorState title="Failed to load dashboard" message={error} />;

  const u = data?.users || { total: 0, byRole: {} };
  const o = data?.orders || { total: 0, byStatus: {} };
  const r = data?.revenue || { totalPaid: 0, paidOrderCount: 0 };

  const maxRev = Math.max(1, ...revenueSeries.map((p) => p.revenue || 0));

  return (
    <>
      <PageHeader
        title={`Welcome back, ${user?.name?.split(' ')[0] || 'Admin'}`}
        subtitle="Full system overview, analytics and controls."
        actions={
          <>
            <Link to="/analytics" className="btn btn-outline"><Activity size={16} /> Analytics</Link>
            <Link to="/audit-logs" className="btn btn-primary">Audit logs</Link>
          </>
        }
      />

      <div className="grid grid-4">
        <StatCard icon={Users}         tone="brand"   label="Total users"     value={formatNumber(u.total)} deltaLabel={`${u.byRole?.USER || 0} customers`} />
        <StatCard icon={Building2}     tone="emerald" label="Clinics"         value={formatNumber(u.byRole?.VENDOR || 0)} />
        <StatCard icon={Package}       tone="indigo"  label="Treatment plans" value={formatNumber(data?.packages?.total || 0)} />
        <StatCard icon={CalendarCheck} tone="amber"   label="Total bookings"  value={formatNumber(o.total)} deltaLabel={`${o.byStatus?.PAID || 0} paid`} />
      </div>

      <div className="grid grid-3" style={{ marginTop: 'var(--s-4)' }}>
        <StatCard icon={CreditCard} tone="emerald" label="Total revenue"   value={formatMoney(r.totalPaid)} suffix="MYR" deltaLabel={`${r.paidOrderCount} paid bookings`} />
        <StatCard icon={LineChart}  tone="brand"   label="Progress logs"   value={formatNumber(data?.reports?.total || 0)} />
        <StatCard icon={Activity}   tone="slate"   label="7-day activity"  value={formatNumber(data?.activity?.last7Days?.orders || 0)} suffix="bookings" deltaLabel={`${data?.activity?.last7Days?.paidPayments || 0} paid`} />
      </div>

      <div className="grid grid-2" style={{ marginTop: 'var(--s-4)' }}>
        <div className="card">
          <div className="card-title">
            <h3>Revenue (last 14 days)</h3>
            <Link to="/analytics" className="text-sm">View report <ArrowRight size={14} /></Link>
          </div>

          {revenueSeries.length === 0 ? (
            <div className="muted">No revenue yet. Once payments start coming in, you'll see them here.</div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 180 }}>
              {revenueSeries.slice(-14).map((p) => (
                <div key={p.period} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                  <div
                    title={`${p.period} · ${formatMoney(p.revenue)}`}
                    style={{
                      width: '100%',
                      height: `${Math.max(2, Math.round((p.revenue / maxRev) * 150))}px`,
                      borderRadius: 6,
                      background: 'linear-gradient(180deg, var(--brand-400), var(--brand-700))'
                    }}
                  />
                  <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{p.period.slice(5)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-title"><h3>Booking pipeline</h3></div>
          <div className="col" style={{ gap: 'var(--s-3)' }}>
            {['PENDING_PAYMENT','PAID','ASSIGNED','IN_PROGRESS','COMPLETED','CANCELLED'].map((s) => {
              const count = o.byStatus?.[s] || 0;
              const pct = o.total ? Math.round((count / o.total) * 100) : 0;
              return (
                <div key={s}>
                  <div className="row between" style={{ marginBottom: 6 }}>
                    <StatusBadge status={s} />
                    <span className="bold">{formatNumber(count)} <span className="muted text-xs">· {pct}%</span></span>
                  </div>
                  <div className="bar-track"><div className="bar-fill" style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="grid grid-2" style={{ marginTop: 'var(--s-4)' }}>
        <div className="card">
          <div className="card-title"><h3>Quick links</h3></div>
          <div className="col" style={{ gap: 'var(--s-2)' }}>
            <Link className="list-row" to="/users">Manage all customers <ArrowRight size={16} /></Link>
            <Link className="list-row" to="/admins">Manage admins / staff <ArrowRight size={16} /></Link>
            <Link className="list-row" to="/vendors">Manage clinics <ArrowRight size={16} /></Link>
            <Link className="list-row" to="/packages">Manage treatment plans <ArrowRight size={16} /></Link>
            <Link className="list-row" to="/payments">Payment tracking <ArrowRight size={16} /></Link>
            <Link className="list-row" to="/audit-logs">Audit logs <ArrowRight size={16} /></Link>
          </div>
        </div>

        <div className="card">
          <div className="card-title"><h3>Roles snapshot</h3></div>
          <div className="col">
            {Object.entries(u.byRole || {}).map(([role, count]) => (
              <div key={role} className="list-row">
                <span className={`role-badge role-${role}`}>{role.replaceAll('_',' ')}</span>
                <span className="bold">{formatNumber(count)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

export default SuperAdminDashboard;
